import { Box, Checkbox, CircularProgress, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  BuildChecklistEntity,
  ChecklistItemEntity,
  PreferenceCardEntity,
} from '@peritasai/repository';
import React from 'react';

const DispatchListContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
}));

const DispatchGroupContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
}));

const DispatchGroupHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(0.5),
  paddingBottom: theme.spacing(1),
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

const DispatchItemContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isSelected' && prop !== 'disabled',
})<{ isSelected?: boolean; disabled?: boolean }>(
  ({ theme, isSelected, disabled }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    padding: theme.spacing(1.5),
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${
      isSelected ? theme.palette.primary.main : theme.palette.divider
    }`,
    backgroundColor: isSelected
      ? theme.palette.primary.light + '10'
      : theme.palette.background.paper,
    cursor: disabled ? 'not-allowed' : 'pointer',
    transition: 'all 0.2s ease',
    '&:hover': {
      borderColor: disabled
        ? theme.palette.divider
        : theme.palette.primary.main,
      backgroundColor: disabled
        ? theme.palette.action.disabledBackground
        : theme.palette.action.hover,
    },
    opacity: disabled ? 0.5 : 1,
  })
);

const DispatchItemInfo = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  gap: 4,
});

const DispatchItemLabel = styled(Typography)(({ theme }) => ({
  ...theme.typography.body2,
  fontWeight: theme.typography.fontWeightMedium,
}));

const DispatchItemDetails = styled(Typography)(({ theme }) => ({
  ...theme.typography.caption,
  color: theme.palette.text.secondary,
}));

const SectionContainer = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(4),
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  ...theme.typography.h6,
  fontWeight: 600,
  marginBottom: theme.spacing(2),
}));

const LoadingOverlay = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: 'rgba(255, 255, 255, 0.8)',
  zIndex: 1,
}));

type PreferenceTrackedAsset =
  PreferenceCardEntity['trackedAssets']['trays'][number];
type PreferenceUntrackedAsset =
  PreferenceCardEntity['untrackedAssets']['supplies'][number];

interface DispatchStepProps {
  preferenceCard: PreferenceCardEntity;
  buildChecklist: BuildChecklistEntity;
  onTraySelect?: (checklistItem: ChecklistItemEntity) => Promise<void>;
  onSingleInstrumentSelect?: (
    checklistItem: ChecklistItemEntity
  ) => Promise<void>;
  onInstrumentSelect?: (checklistItem: ChecklistItemEntity) => Promise<void>;
  onSupplySelect?: (checklistItem: ChecklistItemEntity) => Promise<void>;
  isLoading?: boolean;
}

const TrackedItemList: React.FC<{
  preferenceItems: PreferenceTrackedAsset[];
  sectionTitle: string;
  checklistItemsByCatalogId: Map<string, ChecklistItemEntity[]>;
  onItemSelect?: (checklistItem: ChecklistItemEntity) => Promise<void>;
  isLoading?: boolean;
}> = ({
  preferenceItems,
  sectionTitle,
  checklistItemsByCatalogId,
  onItemSelect,
  isLoading,
}) => {
  const handleItemClick = async (checklistItem?: ChecklistItemEntity) => {
    if (isLoading || !checklistItem || !onItemSelect) return;
    await onItemSelect(checklistItem);
  };

  if (preferenceItems.length === 0) {
    return null;
  }

  return (
    <SectionContainer>
      <SectionTitle>{sectionTitle}</SectionTitle>
      <DispatchListContainer>
        {preferenceItems.map((prefItem) => {
          const checklistItems =
            checklistItemsByCatalogId.get(prefItem.catalogItemId) ?? [];
          const rowCount = Math.max(
            prefItem.quantity || 1,
            checklistItems.length || 0,
            1
          );

          return (
            <DispatchGroupContainer key={prefItem.catalogItemId}>
              <DispatchGroupHeader>
                <Typography variant="h6" fontWeight={600}>
                  {prefItem.name}
                </Typography>
                {prefItem.productNumber && (
                  <Typography variant="caption" color="text.secondary">
                    Product: {prefItem.productNumber}
                    {prefItem.manufacturer && ` • ${prefItem.manufacturer}`}
                  </Typography>
                )}
                <Typography variant="caption" color="text.secondary">
                  Qty Required: {prefItem.quantity}
                </Typography>
              </DispatchGroupHeader>
              {Array.from({ length: rowCount }).map((_, index) => {
                const checklistItem = checklistItems[index];
                const isSelected = checklistItem?.allocated ?? false;
                const isDisabled = !checklistItem;

                return (
                  <DispatchItemContainer
                    key={`${prefItem.catalogItemId}-${index}`}
                    isSelected={isSelected}
                    disabled={isDisabled}
                    onClick={() => handleItemClick(checklistItem)}
                  >
                    <DispatchItemInfo>
                      <DispatchItemLabel>
                        {prefItem.name} #{index + 1}
                      </DispatchItemLabel>
                      {checklistItem?.notes && (
                        <DispatchItemDetails>
                          {checklistItem.notes}
                        </DispatchItemDetails>
                      )}
                      {isDisabled && (
                        <DispatchItemDetails>
                          Checklist item not available yet
                        </DispatchItemDetails>
                      )}
                    </DispatchItemInfo>
                    <Checkbox
                      checked={isSelected}
                      disabled={isDisabled}
                      onChange={() => handleItemClick(checklistItem)}
                      color="primary"
                      onClick={(e) => e.stopPropagation()}
                    />
                  </DispatchItemContainer>
                );
              })}
            </DispatchGroupContainer>
          );
        })}
      </DispatchListContainer>
    </SectionContainer>
  );
};

const UntrackedItemList: React.FC<{
  preferenceItems: PreferenceUntrackedAsset[];
  sectionTitle: string;
  checklistItemsByCatalogId: Map<string, ChecklistItemEntity[]>;
  onItemSelect?: (checklistItem: ChecklistItemEntity) => Promise<void>;
  isLoading?: boolean;
}> = ({
  preferenceItems,
  sectionTitle,
  checklistItemsByCatalogId,
  onItemSelect,
  isLoading,
}) => {
  const handleItemClick = async (checklistItem?: ChecklistItemEntity) => {
    if (isLoading || !checklistItem || !onItemSelect) return;
    await onItemSelect(checklistItem);
  };

  if (preferenceItems.length === 0) {
    return null;
  }

  return (
    <SectionContainer>
      <SectionTitle>{sectionTitle}</SectionTitle>
      <DispatchListContainer>
        {preferenceItems.map((prefItem) => {
          const checklistItems =
            checklistItemsByCatalogId.get(prefItem.catalogId) ?? [];
          const rowCount = Math.max(
            prefItem.quantity || 1,
            checklistItems.length || 0,
            1
          );

          return (
            <DispatchGroupContainer key={prefItem.id}>
              <DispatchGroupHeader>
                <Typography variant="h6" fontWeight={600}>
                  {prefItem.name}
                </Typography>
                {prefItem.productNumber && (
                  <Typography variant="caption" color="text.secondary">
                    Product: {prefItem.productNumber}
                    {prefItem.manufacturer && ` • ${prefItem.manufacturer}`}
                  </Typography>
                )}
                <Typography variant="caption" color="text.secondary">
                  Qty Required: {prefItem.quantity}
                </Typography>
              </DispatchGroupHeader>
              {Array.from({ length: rowCount }).map((_, index) => {
                const checklistItem = checklistItems[index];
                const isSelected = checklistItem?.allocated ?? false;
                const isDisabled = !checklistItem;

                return (
                  <DispatchItemContainer
                    key={`${prefItem.id}-${index}`}
                    isSelected={isSelected}
                    disabled={isDisabled}
                    onClick={() => handleItemClick(checklistItem)}
                  >
                    <DispatchItemInfo>
                      <DispatchItemLabel>
                        {prefItem.name} #{index + 1}
                      </DispatchItemLabel>
                      {checklistItem?.notes && (
                        <DispatchItemDetails>
                          {checklistItem.notes}
                        </DispatchItemDetails>
                      )}
                      {isDisabled && (
                        <DispatchItemDetails>
                          Checklist item not available yet
                        </DispatchItemDetails>
                      )}
                    </DispatchItemInfo>
                    <Checkbox
                      checked={isSelected}
                      disabled={isDisabled}
                      onChange={() => handleItemClick(checklistItem)}
                      color="primary"
                      onClick={(e) => e.stopPropagation()}
                    />
                  </DispatchItemContainer>
                );
              })}
            </DispatchGroupContainer>
          );
        })}
      </DispatchListContainer>
    </SectionContainer>
  );
};

export const DispatchStep: React.FC<DispatchStepProps> = ({
  preferenceCard,
  buildChecklist,
  onTraySelect,
  onSingleInstrumentSelect,
  onInstrumentSelect,
  onSupplySelect,
  isLoading = false,
}) => {
  const trays = preferenceCard.trackedAssets.trays;
  const singleInstruments =
    preferenceCard.untrackedAssets.singlePackedInstruments;
  const instruments = preferenceCard.untrackedAssets.instruments ?? [];
  const supplies = preferenceCard.untrackedAssets.supplies;

  const trayChecklistMap = React.useMemo(() => {
    const map = new Map<string, ChecklistItemEntity[]>();
    buildChecklist.getTrays().forEach((group) => {
      map.set(group.catalogItemId, group.items);
    });
    return map;
  }, [buildChecklist]);

  const singleInstrumentChecklistMap = React.useMemo(() => {
    const map = new Map<string, ChecklistItemEntity[]>();
    buildChecklist.getSinglePackedInstruments().forEach((group) => {
      map.set(group.catalogItemId, group.items);
    });
    return map;
  }, [buildChecklist]);

  const instrumentChecklistMap = React.useMemo(() => {
    const map = new Map<string, ChecklistItemEntity[]>();
    buildChecklist.getUntrackedInstruments().forEach((group) => {
      map.set(group.catalogItemId, group.items);
    });
    return map;
  }, [buildChecklist]);

  const suppliesChecklistMap = React.useMemo(() => {
    const map = new Map<string, ChecklistItemEntity[]>();
    buildChecklist.getSupplies().forEach((group) => {
      map.set(group.catalogItemId, group.items);
    });
    return map;
  }, [buildChecklist]);

  const hasAnyItems =
    trays.length > 0 ||
    singleInstruments.length > 0 ||
    instruments.length > 0 ||
    supplies.length > 0;

  if (!hasAnyItems) {
    return (
      <Box sx={{ padding: 3, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          No items available for dispatch
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ position: 'relative' }}>
      {isLoading && (
        <LoadingOverlay>
          <CircularProgress size={40} />
        </LoadingOverlay>
      )}
      <TrackedItemList
        preferenceItems={trays as PreferenceTrackedAsset[]}
        sectionTitle="Instrument Trays"
        checklistItemsByCatalogId={trayChecklistMap}
        onItemSelect={onTraySelect}
        isLoading={isLoading}
      />
      <UntrackedItemList
        preferenceItems={singleInstruments as PreferenceUntrackedAsset[]}
        sectionTitle="Single Instruments"
        checklistItemsByCatalogId={singleInstrumentChecklistMap}
        onItemSelect={onSingleInstrumentSelect}
        isLoading={isLoading}
      />
      <UntrackedItemList
        preferenceItems={instruments as PreferenceUntrackedAsset[]}
        sectionTitle="Instruments"
        checklistItemsByCatalogId={instrumentChecklistMap}
        onItemSelect={onInstrumentSelect}
        isLoading={isLoading}
      />
      <UntrackedItemList
        preferenceItems={supplies as PreferenceUntrackedAsset[]}
        sectionTitle="Supplies"
        checklistItemsByCatalogId={suppliesChecklistMap}
        onItemSelect={onSupplySelect}
        isLoading={isLoading}
      />
    </Box>
  );
};
